import React from 'react';
import dayjs from 'dayjs';
import { buildSimplePrintDocument } from './simple-document';
import { LINE_HEIGHT_PX } from '../page-template';
import { getCompanyLetterhead } from '../company-letterhead';
import type { PrintDocumentModel, RichPrintRenderPayload } from '../types';
import type { DeliveryPrintData } from '@/services/delivery.service';
import type { CompanyPrintInfo } from '@/components/sales-invoice/PrintableSalesInvoice';
import { formatPrintAmount } from '@/constants/uaDirhamDisplay';
import { formatAmountInWordsPlain } from '@/utils/amountInWords';

function hasValue(v: string | number | null | undefined): boolean {
  if (v == null) return false;
  const s = String(v).trim();
  return s !== '' && s !== '—';
}

function fmtDate(v?: string | null): string {
  return v ? dayjs(v).format('DD-MM-YYYY') : '';
}

function fmtDateTime(v?: string | null): string {
  return v ? dayjs(v).format('DD-MM-YYYY HH:mm') : '';
}

function FieldPairsTable({ fields }: { fields: Array<{ label: string; value: string }> }) {
  if (!fields.length) return null;
  const rows: Array<Array<{ label: string; value: string }>> = [];
  for (let i = 0; i < fields.length; i += 2) {
    rows.push(fields.slice(i, i + 2));
  }
  return (
    <table className="print-dn-field-pairs">
      <tbody>
        {rows.map((pair, ri) => (
          <tr key={ri}>
            <th>{pair[0].label}</th>
            <td>{pair[0].value}</td>
            {pair[1] ? (
              <>
                <th>{pair[1].label}</th>
                <td>{pair[1].value}</td>
              </>
            ) : (
              <>
                <th />
                <td />
              </>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function DnSignatures({
  preparedBy,
  deliveredBy,
  receivedBy,
  receivedDate,
}: {
  preparedBy: string;
  deliveredBy: string;
  receivedBy: string;
  receivedDate: string;
}) {
  return (
    <section className="print-dn-signatures">
      <div className="print-dn-signatures-col">
        <strong>Prepared by</strong>
        <div className="print-dn-sign-detail">{preparedBy || '\u00a0'}</div>
        <div className="print-dn-sign-line">Name &amp; sign</div>
      </div>
      <div className="print-dn-signatures-col">
        <strong>Delivered by</strong>
        <div className="print-dn-sign-detail">{deliveredBy || '\u00a0'}</div>
        <div className="print-dn-sign-line">Sign</div>
      </div>
      <div className="print-dn-signatures-col">
        <strong>Received by</strong>
        <div className="print-dn-sign-detail">{receivedBy || '\u00a0'}</div>
        {receivedDate ? <div className="print-dn-sign-detail">Date: {receivedDate}</div> : null}
        <div className="print-dn-sign-line">Name, stamp &amp; sign</div>
      </div>
    </section>
  );
}

function qtyTotalsBlock(required: number, delivered: number, colSpanLabel = 2) {
  return (
    <table className="print-generic-table print-dn-totals">
      <tbody>
        <tr>
          <td colSpan={colSpanLabel} className="print-dn-totals-label">
            Totals
          </td>
          <td className="num">{required}</td>
          <td className="num">{delivered}</td>
        </tr>
      </tbody>
    </table>
  );
}

export function buildDeliveryNotePrintDocument(
  data: DeliveryPrintData,
  companyInfo: CompanyPrintInfo = { name: 'Company' },
  opts?: { letterhead?: boolean }
): PrintDocumentModel {
  const useLh = opts?.letterhead !== false;
  const docNo = data.delivery_note_number || data.order_number || 'DN';
  const companyTrn = (companyInfo.taxNumber || '').replace(/^TRN:\s*/i, '');
  const subject = `Delivery — ${data.order_number || '—'}`;

  const leftRows = [
    { label: 'M/S', value: data.customer_name || '' },
    ...(hasValue(data.customer_address) ? [{ label: '', value: data.customer_address }] : []),
    { label: 'Subject', value: subject },
  ];

  const rightRows = [
    { label: 'TRN', value: companyTrn },
    { label: 'DN No.', value: docNo },
    { label: 'Date', value: fmtDate(data.delivery_date) },
    { label: 'Customer order', value: data.order_number || '' },
    { label: 'Quotation', value: data.quotation_number || '' },
    { label: 'L.P.O', value: data.customer_po_number || '' },
    { label: 'SAP#', value: data.sap_number || '' },
    { label: 'MFR', value: data.revenue_service_type_label || '' },
    { label: 'Country of Origin', value: data.country_of_origin || '' },
    { label: 'Status', value: data.delivery_status || '' },
  ].filter((r) => hasValue(r.value));

  const lines = (data.items || []).map((row, idx) => {
    const code = row.item_code?.trim() || '';
    const spec = row.specification || '—';
    return {
      lineNumber: row.line_number ?? idx + 1,
      description: spec,
      descriptionChunks: code ? [code, spec] : [spec],
      cells: {
        qtyOrdered: String(row.qty_required ?? ''),
        qtyDelivered: String(row.qty_delivered ?? ''),
      },
    };
  });

  const totalRequired = data.totals?.total_qty_required ?? 0;
  const totalDelivered = data.totals?.total_qty_delivered ?? 0;

  const model = buildSimplePrintDocument({
    docType: 'delivery_note',
    title: 'DELIVERY NOTE',
    documentNumber: docNo,
    partyName: data.customer_name || '',
    templateId: 'letter-letterhead',
    letterhead: useLh ? getCompanyLetterhead() : false,
    continuationLabel: `${docNo} | ${data.customer_name || ''}`,
    partyBlock: { leftRows, rightRows },
    columns: [
      { key: 'sno', label: 'Sr', width: '36px' },
      { key: 'description', label: 'Description' },
      { key: 'qtyOrdered', label: 'Qty ordered', align: 'right', width: '88px' },
      { key: 'qtyDelivered', label: 'Qty delivered', align: 'right', width: '96px' },
    ],
    lines,
    finals: [
      { id: 'totals', heightPx: 36, keepTogetherWithNext: true },
      { id: 'certify', heightPx: 48, keepTogetherWithNext: true },
      { id: 'signatures', heightPx: 110 },
    ],
    renderPayload: {
      finalBlocks: {
        totals: qtyTotalsBlock(totalRequired, totalDelivered),
        certify: (
          <p className="print-dn-certify">
            We certify that the goods described above have been dispatched in good order. Recipient to
            verify quantity and condition on receipt.
          </p>
        ),
        signatures: (
          <DnSignatures
            preparedBy={hasValue(data.prepared_by) ? String(data.prepared_by) : ''}
            deliveredBy={
              hasValue(data.delivery_assignee_name) ? String(data.delivery_assignee_name) : ''
            }
            receivedBy={hasValue(data.received_by) ? String(data.received_by) : ''}
            receivedDate={fmtDate(data.received_date)}
          />
        ),
      },
    },
  });

  return attachPreTableSections(model, buildShipmentFields(data), buildNoteParts(data));
}

function dash(v: string | number | null | undefined): string {
  if (!hasValue(v)) return '—';
  return String(v).trim();
}

function buildShipmentFields(data: DeliveryPrintData): Array<{ label: string; value: string }> {
  const driverDisplay = [data.driver_name, data.driver_phone ? `(${data.driver_phone})` : '']
    .filter(Boolean)
    .join(' ');
  const shipmentFields: Array<{ label: string; value: string }> = [];
  if (hasValue(data.shipping_method)) {
    shipmentFields.push({ label: 'Method', value: String(data.shipping_method) });
  }
  if (hasValue(data.tracking_number)) {
    shipmentFields.push({ label: 'Tracking', value: String(data.tracking_number) });
  }
  if (hasValue(data.vehicle_number)) {
    shipmentFields.push({ label: 'Vehicle', value: String(data.vehicle_number) });
  }
  if (hasValue(driverDisplay)) shipmentFields.push({ label: 'Driver', value: driverDisplay });
  if (hasValue(data.delivery_assignee_name)) {
    shipmentFields.push({ label: 'Delivery man', value: String(data.delivery_assignee_name) });
  }
  if (hasValue(data.in_transit_at)) {
    shipmentFields.push({ label: 'In transit at', value: fmtDateTime(data.in_transit_at) });
  }
  if (hasValue(data.delivered_complete_at)) {
    shipmentFields.push({ label: 'Delivered at', value: fmtDateTime(data.delivered_complete_at) });
  }
  if (hasValue(data.returned_at)) {
    shipmentFields.push({ label: 'Returned at', value: fmtDateTime(data.returned_at) });
  }
  return shipmentFields;
}

function buildNoteParts(data: DeliveryPrintData): Array<{ label: string; value: string }> {
  const noteParts: Array<{ label: string; value: string }> = [];
  if (hasValue(data.special_instructions)) {
    noteParts.push({ label: 'Instructions', value: String(data.special_instructions) });
  }
  if (hasValue(data.notes)) noteParts.push({ label: 'Notes', value: String(data.notes) });
  if (hasValue(data.status_transition_remarks)) {
    noteParts.push({ label: 'Status remarks', value: String(data.status_transition_remarks) });
  }
  return noteParts;
}

function attachPreTableSections(
  model: PrintDocumentModel,
  shipmentFields: Array<{ label: string; value: string }>,
  noteParts: Array<{ label: string; value: string }>
): PrintDocumentModel {
  const payload = (model.renderPayload || {}) as RichPrintRenderPayload;
  const sectionNodes: Record<string, React.ReactNode> = { ...(payload.sectionNodes || {}) };

  if (shipmentFields.length) {
    const shipH = LINE_HEIGHT_PX + Math.ceil(shipmentFields.length / 2) * LINE_HEIGHT_PX + 16;
    model.firstPageSections.push({ id: 'shipment', heightPx: shipH });
    sectionNodes.shipment = (
      <section className="print-dn-shipment">
        <h4>Shipment</h4>
        <FieldPairsTable fields={shipmentFields} />
      </section>
    );
  }

  if (noteParts.length) {
    const notesH =
      noteParts.reduce((h, p) => h + Math.ceil(p.value.length / 90) * LINE_HEIGHT_PX + 8, 8);
    model.firstPageSections.push({ id: 'preNotes', heightPx: Math.max(notesH, 40) });
    sectionNodes.preNotes = (
      <div className="print-dn-prenotes">
        {noteParts.map((p) => (
          <p key={p.label}>
            <strong>{p.label}:</strong> {p.value}
          </p>
        ))}
      </div>
    );
  }

  model.renderPayload = { ...payload, sectionNodes };
  return model;
}

export function buildPackingListPrintDocument(
  data: DeliveryPrintData,
  companyInfo: CompanyPrintInfo = { name: 'Company' },
  opts?: { letterhead?: boolean }
): PrintDocumentModel {
  const useLh = opts?.letterhead !== false;
  const docNo = data.delivery_note_number || 'PL';
  const companyTrn = (companyInfo.taxNumber || '').replace(/^TRN:\s*/i, '');
  const items = data.items || [];

  const leftRows = [
    { label: 'M/S', value: data.customer_name || '' },
    ...(hasValue(data.customer_address) ? [{ label: '', value: data.customer_address }] : []),
    { label: 'Subject', value: `Packing — ${data.order_number || '—'}` },
  ];

  // Keep formal reference grid filled (legacy packing always showed L.P.O / MFR with —).
  const rightRows = [
    { label: 'TRN', value: dash(companyTrn) },
    { label: 'DN No.', value: dash(docNo) },
    { label: 'Date', value: dash(fmtDate(data.delivery_date)) },
    { label: 'Customer order', value: dash(data.order_number) },
    { label: 'Quotation', value: dash(data.quotation_number) },
    { label: 'L.P.O', value: dash(data.customer_po_number) },
    { label: 'SAP#', value: dash(data.sap_number) },
    { label: 'MFR', value: dash(data.revenue_service_type_label) },
    { label: 'Country of Origin', value: dash(data.country_of_origin) },
    { label: 'Status', value: dash(data.delivery_status) },
  ];

  const lines = items.map((row, idx) => {
    const code = row.item_code?.trim() || '';
    const spec = row.specification || '—';
    return {
      lineNumber: row.line_number ?? idx + 1,
      description: spec,
      descriptionChunks: code ? [code, spec] : [spec],
      cells: {
        qtyRequired: String(row.qty_required ?? ''),
        qtyDelivered: String(row.qty_delivered ?? ''),
        weight:
          row.weight != null
            ? `${row.weight}${row.weight_uom ? ` ${row.weight_uom}` : ''}`.trim()
            : '—',
        hsn: dash(row.hsn_code),
      },
    };
  });

  const columns = [
    { key: 'sno', label: 'PO Sr', width: '36px' },
    { key: 'description', label: 'Specification' },
    { key: 'qtyRequired', label: 'Qty required', align: 'right' as const, width: '72px' },
    { key: 'qtyDelivered', label: 'Qty delivered', align: 'right' as const, width: '80px' },
    { key: 'weight', label: 'Weight', align: 'right' as const, width: '64px' },
    { key: 'hsn', label: 'HSN Code', width: '72px' },
  ];

  const packages = data.packages || [];
  const packageBlock =
    packages.length > 0 ? (
      <section className="print-dn-packages">
        <h4>Boxes / Pallets</h4>
        <table className="print-generic-table">
          <thead>
            <tr>
              <th className="sno">#</th>
              <th>Type</th>
              <th className="num">Qty</th>
              <th>Size</th>
              <th className="num">Weight</th>
              <th>Remarks</th>
            </tr>
          </thead>
          <tbody>
            {packages.map((p, i) => (
              <tr key={`${p.line_number}-${i}`}>
                <td className="sno">{p.line_number ?? i + 1}</td>
                <td>{String(p.package_type || '').toLowerCase() === 'pallet' ? 'Pallet' : 'Box'}</td>
                <td className="num">{p.quantity ?? 1}</td>
                <td>{dash(p.size)}</td>
                <td className="num">
                  {p.weight != null
                    ? `${p.weight}${p.weight_uom ? ` ${p.weight_uom}` : ''}`.trim()
                    : '—'}
                </td>
                <td>{dash(p.remarks)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    ) : null;

  const totalsPadCount = 2;
  const totalRequired = data.totals?.total_qty_required ?? 0;
  const totalDelivered = data.totals?.total_qty_delivered ?? 0;

  const finals: Array<{ id: string; heightPx: number; keepTogetherWithNext?: boolean }> = [
    { id: 'totals', heightPx: 36, keepTogetherWithNext: true },
  ];
  if (packages.length > 0) {
    finals.push({
      id: 'packages',
      heightPx: 40 + packages.length * LINE_HEIGHT_PX + 16,
      keepTogetherWithNext: true,
    });
  }
  finals.push(
    { id: 'certify', heightPx: 40, keepTogetherWithNext: true },
    { id: 'signatures', heightPx: 110 }
  );

  const model = buildSimplePrintDocument({
    docType: 'packing_list',
    title: 'PACKING LIST',
    documentNumber: docNo,
    partyName: data.customer_name || '',
    templateId: 'letter-letterhead',
    letterhead: useLh ? getCompanyLetterhead() : false,
    continuationLabel: `Packing ${docNo} | ${data.customer_name || ''}`,
    partyBlock: { leftRows, rightRows },
    columns,
    lines,
    finals,
    renderPayload: {
      finalBlocks: {
        totals: (
          <table className="print-generic-table print-dn-totals print-pl-totals">
            <tbody>
              <tr>
                <td colSpan={2} className="print-dn-totals-label">
                  Totals
                </td>
                <td className="num">{totalRequired}</td>
                <td className="num">{totalDelivered}</td>
                {totalsPadCount > 0 ? <td colSpan={totalsPadCount} /> : null}
              </tr>
            </tbody>
          </table>
        ),
        packages: packageBlock,
        certify: (
          <p className="print-dn-certify">
            I have received the above mentioned items in a good condition.
          </p>
        ),
        signatures: (
          <section className="print-dn-signatures print-pl-signatures">
            <div className="print-dn-signatures-col">
              <strong>Prepared by</strong>
              <div className="print-dn-sign-detail">
                {hasValue(data.prepared_by) ? String(data.prepared_by) : '\u00a0'}
              </div>
              <div className="print-dn-sign-line">Name &amp; sign</div>
            </div>
            <div className="print-dn-signatures-col">
              <strong>Received by</strong>
              <div className="print-dn-sign-detail">
                {hasValue(data.received_by) ? String(data.received_by) : '\u00a0'}
              </div>
              {hasValue(data.received_date) ? (
                <div className="print-dn-sign-detail">Date: {fmtDate(data.received_date)}</div>
              ) : null}
              <div className="print-dn-sign-line">Name, stamp &amp; sign</div>
            </div>
          </section>
        ),
      },
    },
  });

  return attachPreTableSections(model, buildShipmentFields(data), buildNoteParts(data));
}

export function buildCommercialInvoicePrintDocument(
  data: DeliveryPrintData,
  companyInfo: CompanyPrintInfo = { name: 'Company' },
  opts?: { letterhead?: boolean }
): PrintDocumentModel {
  const useLh = opts?.letterhead !== false;
  const docNo = data.delivery_note_number || data.order_number || 'CI';
  const currency = (data.currency || 'AED').trim() || 'AED';
  const companyTrn = (companyInfo.taxNumber || '').replace(/^TRN:\s*/i, '');

  const leftRows = [
    { label: 'M/S', value: data.customer_name || '' },
    ...(hasValue(data.customer_address) ? [{ label: '', value: data.customer_address }] : []),
    { label: 'Subject', value: `Commercial invoice — ${data.order_number || '—'}` },
  ];

  const rightRows = [
    { label: 'TRN', value: dash(companyTrn) },
    { label: 'DN No.', value: dash(docNo) },
    { label: 'Date', value: dash(fmtDate(data.delivery_date)) },
    { label: 'Customer order', value: dash(data.order_number) },
    { label: 'Quotation', value: dash(data.quotation_number) },
    { label: 'L.P.O', value: dash(data.customer_po_number) },
    { label: 'SAP#', value: dash(data.sap_number) },
    { label: 'MFR', value: dash(data.revenue_service_type_label) },
    { label: 'Packing list', value: dash(docNo) },
    { label: 'Country of Origin', value: dash(data.country_of_origin) },
    { label: 'Currency', value: dash(currency) },
  ];

  const lines = (data.items || []).map((row, idx) => {
    const code = row.item_code?.trim() || '';
    const spec = row.specification || '—';
    const qty = Number(row.qty_delivered ?? 0);
    const unit = Number(row.unit_price ?? 0);
    const total = Number(row.line_amount ?? qty * unit);
    return {
      lineNumber: row.line_number ?? idx + 1,
      description: spec,
      descriptionChunks: code ? [code, spec] : [spec],
      cells: {
        hsn: dash(row.hsn_code),
        qty: Number.isFinite(qty) ? String(qty) : '0',
        uom: row.uom || '—',
        unit: formatPrintAmount(unit, currency),
        total: formatPrintAmount(total, currency),
      },
    };
  });

  const packages = data.packages || [];
  const packageBlock =
    packages.length > 0 ? (
      <section className="print-dn-packages">
        <h4>Boxes / Pallets</h4>
        <p className="print-dn-certify" style={{ marginTop: 0, marginBottom: 6 }}>
          Packing details as per packing list {docNo}.
        </p>
        <table className="print-generic-table">
          <thead>
            <tr>
              <th className="sno">#</th>
              <th>Type</th>
              <th className="num">Qty</th>
              <th>Size</th>
              <th className="num">Weight</th>
              <th>Remarks</th>
            </tr>
          </thead>
          <tbody>
            {packages.map((p, i) => (
              <tr key={`${p.line_number}-${i}`}>
                <td className="sno">{p.line_number ?? i + 1}</td>
                <td>{String(p.package_type || '').toLowerCase() === 'pallet' ? 'Pallet' : 'Box'}</td>
                <td className="num">{p.quantity ?? 1}</td>
                <td>{dash(p.size)}</td>
                <td className="num">
                  {p.weight != null
                    ? `${p.weight}${p.weight_uom ? ` ${p.weight_uom}` : ''}`.trim()
                    : '—'}
                </td>
                <td>{dash(p.remarks)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    ) : (
      <p className="print-dn-certify">Packing details as per packing list {docNo}.</p>
    );

  const subtotal = Number(data.totals?.subtotal ?? 0);
  const grand = Number(data.totals?.grand_total != null ? data.totals.grand_total : subtotal);

  const finals: Array<{ id: string; heightPx: number; keepTogetherWithNext?: boolean }> = [
    { id: 'totals', heightPx: 72, keepTogetherWithNext: true },
    { id: 'amountWords', heightPx: 36, keepTogetherWithNext: true },
    {
      id: 'packages',
      heightPx: packages.length > 0 ? 56 + packages.length * LINE_HEIGHT_PX + 24 : 36,
      keepTogetherWithNext: true,
    },
    { id: 'signatures', heightPx: 110 },
  ];

  const model = buildSimplePrintDocument({
    docType: 'commercial_invoice',
    title: 'COMMERCIAL INVOICE',
    documentNumber: docNo,
    partyName: data.customer_name || '',
    templateId: 'letter-letterhead',
    letterhead: useLh ? getCompanyLetterhead() : false,
    continuationLabel: `CI ${docNo} | ${data.customer_name || ''}`,
    partyBlock: { leftRows, rightRows },
    columns: [
      { key: 'sno', label: 'Sr', width: '36px' },
      { key: 'description', label: 'Description' },
      { key: 'hsn', label: 'HSN Code', width: '72px' },
      { key: 'qty', label: 'Qty', align: 'right', width: '52px' },
      { key: 'uom', label: 'UOM', width: '40px' },
      { key: 'unit', label: 'Unit Price', align: 'right', width: '72px' },
      { key: 'total', label: 'Total', align: 'right', width: '72px' },
    ],
    lines,
    finals,
    renderPayload: {
      finalBlocks: {
        totals: (
          <div className="print-letter-totals">
            <table>
              <tbody>
                <tr>
                  <td>Subtotal :</td>
                  <td>{formatPrintAmount(subtotal, currency)}</td>
                </tr>
                <tr className="grand-total">
                  <td>Grand Total :</td>
                  <td>{formatPrintAmount(grand, currency)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        ),
        amountWords: (
          <p className="print-letter-amount-words">
            <span>Total Only:</span>
            <strong>
              ({currency}) {formatAmountInWordsPlain(grand)}
            </strong>
          </p>
        ),
        packages: packageBlock,
        signatures: (
          <section className="print-dn-signatures print-pl-signatures">
            <div className="print-dn-signatures-col">
              <strong>Prepared by</strong>
              <div className="print-dn-sign-detail">
                {hasValue(data.prepared_by) ? String(data.prepared_by) : '\u00a0'}
              </div>
              <div className="print-dn-sign-line">Name &amp; sign</div>
            </div>
            <div className="print-dn-signatures-col">
              <strong>Received by</strong>
              <div className="print-dn-sign-detail">
                {hasValue(data.received_by) ? String(data.received_by) : '\u00a0'}
              </div>
              {hasValue(data.received_date) ? (
                <div className="print-dn-sign-detail">Date: {fmtDate(data.received_date)}</div>
              ) : null}
              <div className="print-dn-sign-line">Name, stamp &amp; sign</div>
            </div>
          </section>
        ),
      },
    },
  });

  return attachPreTableSections(model, buildShipmentFields(data), buildNoteParts(data));
}

